import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  type CalendarDayFilterSection,
  type CalendarDayFilters,
  type CalendarMonthFilterSection,
  type CalendarMonthFilters,
  type CalendarMonthHeatmap,
  type CalendarView,
  type CalendarViewMode,
  type CalendarWeekFilterSection,
  type CalendarWeekFilters,
} from '@elva/ui';
import { hidesWholeDayStatusAxis, hidesWholeWeekStatusAxis } from '../api/calendarFilterParams';

/**
 * Options-drawer filters, kept in the URL query string so a filtered view survives a reload,
 * can be shared (Day / Week / Month use separate keys, all cleared when the view changes).
 * The calendar queries turn them into request parameters; the server does the filtering.
 */
const KEY = {
  view: 'view',
  viewMode: 'vm',
  dayQuick: 'q',
  dayStatus: 'hs',
  dayProviders: 'hp',
  dayOperatories: 'ho',
  dayFlags: 'fl',
  dayMinProduction: 'min',
  weekReadiness: 'wr',
  weekStatus: 'ws',
  weekRange: 'rd',
  monthHighlight: 'mh',
  monthHeatmap: 'mm',
  monthCells: 'mc',
} as const;

const DAY_KEYS = [
  KEY.dayQuick,
  KEY.dayStatus,
  KEY.dayProviders,
  KEY.dayOperatories,
  KEY.dayFlags,
  KEY.dayMinProduction,
] as const;
const WEEK_KEYS = [KEY.weekReadiness, KEY.weekStatus, KEY.weekRange] as const;
const MONTH_KEYS = [KEY.monthHighlight, KEY.monthHeatmap, KEY.monthCells] as const;

const VIEWS: readonly CalendarView[] = ['Day', 'Week', 'Month'];
const DAY_QUICK_IDS = ['all', 'red', 'unconfirmed', 'notready', 'building', 'newpt'] as const;
const WEEK_READINESS_IDS = ['all', 'yr', 'red'] as const;
const MONTH_HIGHLIGHT_IDS = ['all', 'under', 'issues'] as const;
const HEATMAP_IDS: readonly CalendarMonthHeatmap[] = ['goal', 'new', 'noshow'];
const MAX_MIN_PRODUCTION = 5000;

function oneOf<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.find((item) => item === value) ?? fallback;
}

function readCsv(params: URLSearchParams, key: string): Set<string> {
  const raw = params.get(key);
  if (!raw) return new Set();
  return new Set(
    raw
      .split(',')
      .filter(Boolean)
      .map((part) => decodeURIComponent(part)),
  );
}

function writeCsv(params: URLSearchParams, key: string, values: ReadonlySet<string>): void {
  if (values.size === 0) {
    params.delete(key);
    return;
  }
  params.set(key, [...values].map((value) => encodeURIComponent(value)).join(','));
}

function toggled(values: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(values);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

function readMinProduction(params: URLSearchParams): number {
  const value = Number(params.get(KEY.dayMinProduction));
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(Math.round(value), MAX_MIN_PRODUCTION);
}

export function useCalendarFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();

  const update = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const view = oneOf(searchParams.get(KEY.view), VIEWS, 'Day');
  const viewMode: CalendarViewMode =
    searchParams.get(KEY.viewMode) === 'Operatory' ? 'Operatory' : 'Provider';

  // `search` is the memo key: the URLSearchParams object itself is new on every render.
  const dayFilters = useMemo<CalendarDayFilters>(() => {
    const params = new URLSearchParams(search);
    return {
      quick: oneOf(params.get(KEY.dayQuick), DAY_QUICK_IDS, 'all'),
      hiddenStatuses: readCsv(params, KEY.dayStatus),
      providers: readCsv(params, KEY.dayProviders),
      operatories: readCsv(params, KEY.dayOperatories),
      flags: readCsv(params, KEY.dayFlags),
      minProduction: readMinProduction(params),
    };
  }, [search]);

  const weekFilters = useMemo<CalendarWeekFilters>(() => {
    const params = new URLSearchParams(search);
    return {
      readiness: oneOf(params.get(KEY.weekReadiness), WEEK_READINESS_IDS, 'all'),
      hiddenStatuses: readCsv(params, KEY.weekStatus),
      rangeDays: params.get(KEY.weekRange) === '14' ? 14 : 7,
    };
  }, [search]);

  const monthFilters = useMemo<CalendarMonthFilters>(() => {
    const params = new URLSearchParams(search);
    return {
      highlight: oneOf(params.get(KEY.monthHighlight), MONTH_HIGHLIGHT_IDS, 'all'),
      heatmap: oneOf(params.get(KEY.monthHeatmap), HEATMAP_IDS, 'goal'),
      hiddenCellParts: readCsv(params, KEY.monthCells),
    };
  }, [search]);

  /** Switching tabs starts the new view from a clean slate: every filter is cleared. */
  const setView = useCallback(
    (next: CalendarView) => {
      update((params) => {
        if (next !== oneOf(params.get(KEY.view), VIEWS, 'Day')) {
          [...DAY_KEYS, ...WEEK_KEYS, ...MONTH_KEYS].forEach((key) => {
            params.delete(key);
          });
        }
        if (next === 'Day') params.delete(KEY.view);
        else params.set(KEY.view, next);
      });
    },
    [update],
  );

  const setViewMode = useCallback(
    (next: CalendarViewMode) => {
      update((params) => {
        if (next === 'Provider') params.delete(KEY.viewMode);
        else params.set(KEY.viewMode, next);
      });
    },
    [update],
  );

  // ── Day ───────────────────────────────────────────────────────────────────
  const setQuick = useCallback(
    (id: string) => {
      update((params) => {
        if (id === 'all') params.delete(KEY.dayQuick);
        else params.set(KEY.dayQuick, id);
      });
    },
    [update],
  );

  const setMinProduction = useCallback(
    (value: number) => {
      update((params) => {
        if (value <= 0) params.delete(KEY.dayMinProduction);
        else params.set(KEY.dayMinProduction, String(Math.min(Math.round(value), MAX_MIN_PRODUCTION)));
      });
    },
    [update],
  );

  /** Un-ticking the last remaining value of a status axis is ignored — it would hide everything. */
  const toggleStatus = useCallback(
    (id: string) => {
      update((params) => {
        const next = toggled(readCsv(params, KEY.dayStatus), id);
        if (hidesWholeDayStatusAxis(next)) return;
        writeCsv(params, KEY.dayStatus, next);
      });
    },
    [update],
  );

  /**
   * Providers / operatories are stored as the ticked ids (empty = all). `allIds` is the full
   * roster, so un-ticking one row from "all" selects the rest. The last ticked row stays ticked.
   */
  const toggleColumn = useCallback(
    (key: typeof KEY.dayProviders | typeof KEY.dayOperatories, id: string, allIds: readonly string[]) => {
      update((params) => {
        const stored = readCsv(params, key);
        const effective = stored.size === 0 ? new Set(allIds) : stored;
        const next = toggled(effective, id);
        if (next.size === 0) return;
        const coversRoster = allIds.length > 0 && allIds.every((rosterId) => next.has(rosterId));
        writeCsv(params, key, coversRoster ? new Set() : next);
      });
    },
    [update],
  );

  const toggleProvider = useCallback(
    (id: string, allIds: readonly string[]) => {
      toggleColumn(KEY.dayProviders, id, allIds);
    },
    [toggleColumn],
  );

  const toggleOperatory = useCallback(
    (id: string, allIds: readonly string[]) => {
      toggleColumn(KEY.dayOperatories, id, allIds);
    },
    [toggleColumn],
  );

  const toggleFlag = useCallback(
    (id: string) => {
      update((params) => {
        writeCsv(params, KEY.dayFlags, toggled(readCsv(params, KEY.dayFlags), id));
      });
    },
    [update],
  );

  const clearDaySection = useCallback(
    (section: CalendarDayFilterSection) => {
      const key: Record<CalendarDayFilterSection, string> = {
        quick: KEY.dayQuick,
        status: KEY.dayStatus,
        providers: KEY.dayProviders,
        operatories: KEY.dayOperatories,
        flags: KEY.dayFlags,
        production: KEY.dayMinProduction,
      };
      update((params) => {
        params.delete(key[section]);
      });
    },
    [update],
  );

  const resetDayFilters = useCallback(
    (alsoViewMode = false) => {
      update((params) => {
        DAY_KEYS.forEach((key) => {
          params.delete(key);
        });
        if (alsoViewMode) params.delete(KEY.viewMode);
      });
    },
    [update],
  );

  // ── Week ──────────────────────────────────────────────────────────────────
  const setWeekReadiness = useCallback(
    (id: string) => {
      update((params) => {
        if (id === 'all') params.delete(KEY.weekReadiness);
        else params.set(KEY.weekReadiness, id);
      });
    },
    [update],
  );

  const setWeekRange = useCallback(
    (days: 7 | 14) => {
      update((params) => {
        if (days === 7) params.delete(KEY.weekRange);
        else params.set(KEY.weekRange, String(days));
      });
    },
    [update],
  );

  /** Confirmed + Pending together (or No show + In treatment + …) would empty an axis, so it is ignored. */
  const toggleWeekStatus = useCallback(
    (id: string) => {
      update((params) => {
        const next = toggled(readCsv(params, KEY.weekStatus), id);
        if (hidesWholeWeekStatusAxis(next)) return;
        writeCsv(params, KEY.weekStatus, next);
      });
    },
    [update],
  );

  const clearWeekSection = useCallback(
    (section: CalendarWeekFilterSection) => {
      const key: Record<CalendarWeekFilterSection, string> = {
        readiness: KEY.weekReadiness,
        status: KEY.weekStatus,
        range: KEY.weekRange,
      };
      update((params) => {
        params.delete(key[section]);
      });
    },
    [update],
  );

  const resetWeekFilters = useCallback(() => {
    update((params) => {
      WEEK_KEYS.forEach((key) => {
        params.delete(key);
      });
    });
  }, [update]);

  // ── Month ─────────────────────────────────────────────────────────────────
  const setMonthHighlight = useCallback(
    (id: string) => {
      update((params) => {
        if (id === 'all') params.delete(KEY.monthHighlight);
        else params.set(KEY.monthHighlight, id);
      });
    },
    [update],
  );

  const setMonthHeatmap = useCallback(
    (heatmap: CalendarMonthHeatmap) => {
      update((params) => {
        if (heatmap === 'goal') params.delete(KEY.monthHeatmap);
        else params.set(KEY.monthHeatmap, heatmap);
      });
    },
    [update],
  );

  const toggleMonthCellPart = useCallback(
    (part: string) => {
      update((params) => {
        writeCsv(params, KEY.monthCells, toggled(readCsv(params, KEY.monthCells), part));
      });
    },
    [update],
  );

  const clearMonthSection = useCallback(
    (section: CalendarMonthFilterSection) => {
      const key: Record<CalendarMonthFilterSection, string> = {
        highlight: KEY.monthHighlight,
        heatmap: KEY.monthHeatmap,
        cells: KEY.monthCells,
      };
      update((params) => {
        params.delete(key[section]);
      });
    },
    [update],
  );

  const resetMonthFilters = useCallback(() => {
    update((params) => {
      MONTH_KEYS.forEach((key) => {
        params.delete(key);
      });
    });
  }, [update]);

  return {
    view,
    setView,
    viewMode,
    setViewMode,
    dayFilters,
    weekFilters,
    monthFilters,
    setQuick,
    setMinProduction,
    toggleStatus,
    toggleProvider,
    toggleOperatory,
    toggleFlag,
    clearDaySection,
    resetDayFilters,
    setWeekReadiness,
    setWeekRange,
    toggleWeekStatus,
    clearWeekSection,
    resetWeekFilters,
    setMonthHighlight,
    setMonthHeatmap,
    toggleMonthCellPart,
    clearMonthSection,
    resetMonthFilters,
  };
}
